import React, { useState, useMemo, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box, Typography, TextField, Button, Select, MenuItem,
  FormControl, Divider,
} from '@material-ui/core';
import { calculateLaborHours } from '../../../SiteTakeoff/laborCalculator';
import { priceTakeoff } from '../../../SiteTakeoff/pricing/priceTakeoff';
import { buildRateOverrides } from '../../../SiteTakeoff/pricing/buildRateOverrides';
import CalcTracePanel from '../../../SiteTakeoff/CalcTracePanel';
import {
  LABOR_CATEGORIES,
  IRRIGATION_FREQUENCY_OPTIONS,
  HERBICIDE_FREQUENCY_OPTIONS,
} from '../../../SiteTakeoff/siteTakeoffConstants';

const useStyles = makeStyles(() => ({
  empty: {
    padding: 32,
    textAlign: 'center',
    color: '#999',
    fontSize: 13,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 10,
  },
  rateRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  },
  provenanceChip: {
    fontSize: 10,
    fontWeight: 600,
    color: '#fff',
    background: '#90a4ae',
    borderRadius: 10,
    padding: '1px 7px',
    whiteSpace: 'nowrap',
  },
  freqRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  },
  freqLabel: {
    fontSize: 12,
    color: '#555',
    minWidth: 90,
    flexShrink: 0,
  },
  catHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '5px 8px',
    borderRadius: 4,
    marginTop: 10,
    marginBottom: 4,
  },
  catTitle: {
    fontWeight: 700,
    fontSize: '0.82rem',
    flex: 1,
  },
  breakdownRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.76rem',
    padding: '2px 8px',
    color: '#555',
    borderBottom: '1px solid #f0f0f0',
  },
  totalsBox: {
    background: '#f5f5f5',
    borderRadius: 6,
    padding: '10px 12px',
    marginTop: 12,
  },
  totalLine: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  totalLabel: {
    fontSize: 12,
    color: '#666',
  },
  totalValue: {
    fontWeight: 700,
    fontSize: 16,
    color: '#222',
  },
  monthlyLine: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  monthlyLabel: {
    fontSize: 11,
    color: '#888',
  },
  monthlyValue: {
    fontSize: 13,
    fontWeight: 500,
    color: '#555',
  },
  createBtn: {
    marginTop: 16,
    width: '100%',
  },
}));

const MTC_FREQUENCY_OPTIONS = [
  { value: 52, label: 'Weekly' },
  { value: 26, label: 'Bi-weekly' },
  { value: 12, label: 'Monthly' },
  { value: 4,  label: 'Quarterly' },
  { value: 2,  label: 'Semi-annual' },
  { value: 1,  label: 'Annual' },
];

const fmtUSD = (n) => (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

const RATE_FIELDS = [
  { key: 'mtc',        ariaLabel: 'Maintenance rate' },
  { key: 'arborist',   ariaLabel: 'Arborist rate' },
  { key: 'herbicide',  ariaLabel: 'Herbicide rate' },
  { key: 'irrigation', ariaLabel: 'Irrigation rate' },
];

const EstimateMode = ({ areas = [], rateOverrides = {}, seed = {}, onCreate = () => {} }) => {
  const classes = useStyles();

  const seedRates = (seed && seed.rates) || {};
  const seedProvenance = (seed && seed.provenance) || {};

  const [rates, setRates] = useState({
    mtc:        seedRates.mtc        != null ? seedRates.mtc        : 0,
    arborist:   seedRates.arborist   != null ? seedRates.arborist   : 0,
    herbicide:  seedRates.herbicide  != null ? seedRates.herbicide  : 0,
    irrigation: seedRates.irrigation != null ? seedRates.irrigation : 0,
  });

  // Sync rate inputs when the async seed arrives after mount, or when jobsite changes.
  // This intentionally overwrites local edits — correct behaviour on jobsite switch.
  useEffect(() => {
    if (seed && seed.rates && Object.keys(seed.rates).length > 0) {
      setRates(seed.rates);
    }
  }, [seed]);

  const [margin, setMargin] = useState(0.5);
  const [mtcVisits, setMtcVisits] = useState(52);
  const [irrigationVisits, setIrrigationVisits] = useState(12);
  const [herbicideVisits, setHerbicideVisits] = useState(4);

  const priced = useMemo(() => {
    const ro = buildRateOverrides({ mtcVisits, irrigationVisits, herbicideVisits }, rateOverrides);
    const hours = calculateLaborHours(areas, ro);
    return priceTakeoff(hours, { rates, margin });
  }, [areas, rateOverrides, rates, margin, mtcVisits, irrigationVisits, herbicideVisits]);

  if (priced.groups.length === 0) {
    return (
      <div className={classes.empty}>
        No services selected on these areas — tag areas in the Areas tab first.
      </div>
    );
  }

  const handleRateChange = (key, value) => {
    setRates(r => ({ ...r, [key]: Number(value) }));
  };

  const handleMarginChange = (e) => {
    const pct = Math.min(95, Math.max(0, Number(e.target.value) || 0));
    setMargin(pct / 100);
  };

  return (
    <Box>
      {/* ── Zone 1: Rates & Margin ── */}
      <Typography className={classes.sectionTitle}>Hourly Rates</Typography>

      {RATE_FIELDS.map(({ key, ariaLabel }) => {
        const catInfo = LABOR_CATEGORIES[key] || {};
        const prov = seedProvenance[key];
        return (
          <div key={key} className={classes.rateRow}>
            <TextField
              label={catInfo.label || ariaLabel}
              type="number"
              size="small"
              value={rates[key]}
              onChange={(e) => handleRateChange(key, e.target.value)}
              inputProps={{ 'aria-label': ariaLabel, min: 0, step: 1 }}
              style={{ flex: 1 }}
            />
            {prov && (
              <span className={classes.provenanceChip}>{prov}</span>
            )}
          </div>
        );
      })}

      <div className={classes.rateRow}>
        <TextField
          label="Margin %"
          type="number"
          size="small"
          value={Math.round(margin * 100)}
          onChange={handleMarginChange}
          inputProps={{ 'aria-label': 'Margin percent', min: 0, max: 95, step: 1 }}
          style={{ flex: 1 }}
        />
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* ── Zone 2: Frequencies ── */}
      <Typography className={classes.sectionTitle}>Visit Frequencies</Typography>

      <div className={classes.freqRow}>
        <span className={classes.freqLabel}>Maintenance</span>
        <FormControl size="small" style={{ flex: 1 }}>
          <Select
            value={mtcVisits}
            onChange={(e) => setMtcVisits(e.target.value)}
            inputProps={{ 'aria-label': 'Maintenance visits per year' }}
          >
            {MTC_FREQUENCY_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className={classes.freqRow}>
        <span className={classes.freqLabel}>Irrigation</span>
        <FormControl size="small" style={{ flex: 1 }}>
          <Select
            value={irrigationVisits}
            onChange={(e) => setIrrigationVisits(e.target.value)}
            inputProps={{ 'aria-label': 'Irrigation visits per year' }}
          >
            {IRRIGATION_FREQUENCY_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className={classes.freqRow}>
        <span className={classes.freqLabel}>Herbicide</span>
        <FormControl size="small" style={{ flex: 1 }}>
          <Select
            value={herbicideVisits}
            onChange={(e) => setHerbicideVisits(e.target.value)}
            inputProps={{ 'aria-label': 'Herbicide visits per year' }}
          >
            {HERBICIDE_FREQUENCY_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* ── Zone 3: Breakdown ── */}
      <Typography className={classes.sectionTitle}>Priced Breakdown</Typography>

      {priced.groups.map((g) => {
        const catInfo = LABOR_CATEGORIES[g.category] || {};
        return (
          <div key={g.category}>
            <div
              className={classes.catHeader}
              style={{ background: catInfo.color ? `${catInfo.color}18` : '#f5f5f5' }}
            >
              <span
                className={classes.catTitle}
                style={{ color: catInfo.color || '#333' }}
              >
                {catInfo.label || g.category}
              </span>
            </div>

            <div className={classes.breakdownRow}>
              <span>{g.annualHours.toFixed(1)} hrs/yr</span>
              <span>${fmtUSD(g.annualCost)} cost</span>
              <span style={{ fontWeight: 600 }}>${fmtUSD(g.annualPrice)}</span>
            </div>
          </div>
        );
      })}

      {/* Totals */}
      <div className={classes.totalsBox}>
        <div className={classes.totalLine}>
          <span className={classes.totalLabel}>Annual Price</span>
          <span className={classes.totalValue} data-testid="estimate-annual-price">
            ${fmtUSD(priced.totals.annualPrice)}
          </span>
        </div>
        <div className={classes.monthlyLine}>
          <span className={classes.monthlyLabel}>Monthly</span>
          <span className={classes.monthlyValue}>${fmtUSD(priced.totals.monthlyPrice)}/mo</span>
        </div>
      </div>

      {/* Read-only calculation ledger (Tier-1 quantities + Tier-2 per-line cost). */}
      <CalcTracePanel trace={priced.trace} />

      {/* Create button — wired to the server create flow (Phase 2). */}
      <Button
        variant="contained"
        color="primary"
        className={classes.createBtn}
        onClick={() => onCreate({
          rates,
          margin,
          frequencies: { mtcVisits, irrigationVisits, herbicideVisits },
        })}
      >
        Create Estimate
      </Button>
    </Box>
  );
};

export default EstimateMode;
